import React, { useEffect, useState, useCallback, useRef } from 'react';
import DialogCard from './DialogCard';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { canShowPurchase } from '../utils/platform';
import { track } from '../utils/analytics';
import { copyText } from '../utils/clipboard';
import { BackIcon } from './icons';
import ColorPicker from './ColorPicker';

// Three things this page had no words for, because it never admitted they
// could happen: a copy that did not work, a read that failed, an action the
// server refused.
// A tap that does nothing is the worst of the three possible answers.
const RENAME_ONLY_ADMIN = {
  it: 'Solo chi ha creato questo calendario può cambiargli nome.',
  en: 'Only the person who created this calendar can rename it.',
  es: 'Solo quien creó este calendario puede cambiarle el nombre.',
  de: 'Nur wer diesen Kalender erstellt hat, kann ihn umbenennen.',
  fr: 'Seule la personne qui a créé ce calendrier peut le renommer.',
  nl: 'Alleen wie deze agenda heeft gemaakt, kan de naam wijzigen.',
  pt: 'Só quem criou este calendário lhe pode mudar o nome.',
  ru: 'Переименовать этот календарь может только тот, кто его создал.',
  pl: 'Nazwę tego kalendarza może zmienić tylko osoba, która go utworzyła.',
  ro: 'Doar persoana care a creat acest calendar îi poate schimba numele.',
  zh: '只有创建这个日历的人才能改名。',
  ja: 'このカレンダーの名前を変えられるのは、作成した人だけです。',
  hi: 'इस कैलेंडर का नाम केवल वही बदल सकता है जिसने इसे बनाया है।',
  ko: '이 캘린더의 이름은 만든 사람만 바꿀 수 있습니다.',
  ar: 'لا يمكن تغيير اسم هذا التقويم إلا لمن أنشأه.',
};

// Shown when an admin hovers or long-presses the name.
const RENAME_HINT = {
  it: 'Tocca per rinominare',      en: 'Tap to rename',        es: 'Toca para renombrar',
  de: 'Zum Umbenennen tippen',     fr: 'Touchez pour renommer', nl: 'Tik om te hernoemen',
  pt: 'Toca para renomear',        ru: 'Нажмите, чтобы переименовать', pl: 'Dotknij, aby zmienić nazwę',
  ro: 'Atinge pentru redenumire',  zh: '点按以重命名',           ja: 'タップして名前を変更',
  hi: 'नाम बदलने के लिए टैप करें',   ko: '탭하여 이름 변경',        ar: 'اضغط لإعادة التسمية',
};

const LOCAL = {
  it: { copyFailed: 'Non sono riuscita a copiarlo. Il codice è qui sopra: leggilo o scrivilo a mano.', membersFailed: 'Non sono riuscita a leggere chi c’è in questo calendario.', actionFailed: 'Non ha funzionato. Controlla la connessione e riprova.' },
  en: { copyFailed: "Couldn't copy it. The code is right above: read it out or type it by hand.", membersFailed: "Couldn't read who is in this calendar.", actionFailed: "That didn't work. Check your connection and try again." },
  es: { copyFailed: 'No he podido copiarlo. El código está arriba: léelo o escríbelo a mano.', membersFailed: 'No he podido leer quién está en este calendario.', actionFailed: 'No ha funcionado. Comprueba la conexión e inténtalo de nuevo.' },
  de: { copyFailed: 'Kopieren hat nicht geklappt. Der Code steht oben: vorlesen oder abtippen.', membersFailed: 'Konnte nicht lesen, wer in diesem Kalender ist.', actionFailed: 'Hat nicht geklappt. Prüfe die Verbindung und versuche es erneut.' },
  fr: { copyFailed: "La copie n'a pas fonctionné. Le code est juste au-dessus : lisez-le ou tapez-le.", membersFailed: "Impossible de lire qui est dans cet agenda.", actionFailed: "Ça n'a pas marché. Vérifiez la connexion et réessayez." },
  nl: { copyFailed: 'Kopiëren lukte niet. De code staat hierboven: lees hem voor of typ hem over.', membersFailed: 'Kon niet lezen wie in deze agenda zit.', actionFailed: 'Dat lukte niet. Controleer je verbinding en probeer opnieuw.' },
  pt: { copyFailed: 'Não consegui copiar. O código está acima: lê-o ou escreve-o à mão.', membersFailed: 'Não consegui ler quem está neste calendário.', actionFailed: 'Não funcionou. Verifica a ligação e tenta de novo.' },
  ru: { copyFailed: 'Скопировать не удалось. Код выше: продиктуйте или впишите вручную.', membersFailed: 'Не удалось прочитать, кто в этом календаре.', actionFailed: 'Не получилось. Проверьте соединение и попробуйте снова.' },
  pl: { copyFailed: 'Nie udało się skopiować. Kod jest powyżej: podyktuj go lub przepisz.', membersFailed: 'Nie udało się odczytać, kto jest w tym kalendarzu.', actionFailed: 'Nie udało się. Sprawdź połączenie i spróbuj ponownie.' },
  ro: { copyFailed: 'Nu am putut copia. Codul este mai sus: citește-l sau scrie-l de mână.', membersFailed: 'Nu am putut citi cine este în acest calendar.', actionFailed: 'Nu a mers. Verifică conexiunea și încearcă din nou.' },
  zh: { copyFailed: '复制失败。代码就在上方：可以念出来或手动输入。', membersFailed: '无法读取此日历的成员。', actionFailed: '没有成功。请检查网络后重试。' },
  ja: { copyFailed: 'コピーできませんでした。コードは上にあります。読み上げるか手で入力してください。', membersFailed: 'このカレンダーの参加者を読み込めませんでした。', actionFailed: 'うまくいきませんでした。接続を確認して再試行してください。' },
  hi: { copyFailed: 'कॉपी नहीं हो सका। कोड ऊपर है: बोलकर बताएँ या हाथ से लिखें।', membersFailed: 'इस कैलेंडर में कौन है, यह नहीं पढ़ा जा सका।', actionFailed: 'काम नहीं हुआ। कनेक्शन जाँचकर फिर कोशिश करें।' },
  ko: { copyFailed: '복사하지 못했습니다. 코드는 바로 위에 있습니다. 불러주거나 직접 입력하세요.', membersFailed: '이 캘린더의 참여자를 불러오지 못했습니다.', actionFailed: '실패했습니다. 연결을 확인하고 다시 시도하세요.' },
  ar: { copyFailed: 'تعذّر النسخ. الرمز في الأعلى: اقرأه أو اكتبه يدويًا.', membersFailed: 'تعذّرت قراءة من في هذا التقويم.', actionFailed: 'لم تنجح العملية. تحقق من الاتصال وحاول مجددًا.' },
};

// ─── Avatar initials ──────────────────────────────────────────────────────────
function Avatar({ src, name, size = 30 }) {
  const initial = (name || '?')[0].toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        referrerPolicy="no-referrer"
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'var(--color-primary)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 700, letterSpacing: 0,
    }}>
      {initial}
    </div>
  );
}

// ─── Copy to clipboard ────────────────────────────────────────────────────────
// The copying itself lives in utils/clipboard.js — see the note there on the
// three different ways this used to fail quietly. `status`: 'idle'|'ok'|'fail'.
function useClipboard(timeout = 2500) {
  const [status, setStatus] = useState('idle');

  async function copy(text) {
    const ok = await copyText(text);
    setStatus(ok ? 'ok' : 'fail');
    setTimeout(() => setStatus('idle'), timeout);
  }

  return { status, copied: status === 'ok', copy };
}

// ─── Member list ──────────────────────────────────────────────────────────────
function MemberList({ groupId, T, L }) {
  const { user } = useAuth();
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed]   = useState(false);
  // Which member's palette is open, if any.
  const [colorFor, setColorFor] = useState(null);

  function setMemberColor(userId, color) {
    setMembers(list => (list || []).map(m => (m.id === userId ? { ...m, color } : m)));
  }

  useEffect(() => {
    let cancelled = false;
    api.get(`/groups/${groupId}/members`)
      .then(r => { if (!cancelled) { setMembers(r.data); setFailed(false); } })
      // A failed read used to become an empty array, and an empty array made
      // the whole section disappear — a shared calendar looking as if nobody
      // were in it.
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupId]);

  if (loading) return <div className="spinner" style={{ margin: '12px auto' }} />;
  if (failed) {
    return (
      <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
        {L.membersFailed}
      </div>
    );
  }
  if (!members?.length) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 10,
      }}>
        {T.members}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {members.map(m => (
          <div key={m.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* The dot IS the colour this person's events are drawn in, and it
                opens the palette. Until now the colour was dealt out by join
                order, so somebody in two shared calendars had two colours and
                neither was anybody's choice — which is exactly what Silvia saw
                when Mirko came out violet in one place and blue in another. */}
            <button
              type="button"
              onClick={() => setColorFor(colorFor === m.id ? null : m.id)}
              aria-label={m.name}
              title={m.name}
              style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                background: m.color || 'var(--color-border)',
                border: colorFor === m.id ? '3px solid var(--color-text)' : '2px solid var(--color-card)',
                boxShadow: '0 0 0 1px var(--color-border)',
                cursor: 'pointer', padding: 0,
              }}
            />
            <Avatar src={m.picture} name={m.name} size={30} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '0.88rem', fontWeight: 600, color: 'var(--color-text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {m.name}
                {m.isMe && (
                  <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginInlineStart: 5 }}>
                    {T.you}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 1 }}>
                {m.email}
              </div>
            </div>
            {m.isAdmin && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--color-primary)',
                border: '1px solid var(--color-primary)',
                borderRadius: 4, padding: '2px 7px', flexShrink: 0,
              }}>
                {T.admin}
              </span>
            )}
          </div>
          {colorFor === m.id && (
            <div style={{ padding: '10px 0 4px 40px' }}>
              <ColorPicker
                lang={user?.language}
                value={m.color || null}
                onChange={async (c) => {
                  const before = m.color;
                  // Shown at once and put back if the server refuses: choosing
                  // a colour should feel like choosing, not like submitting.
                  setMemberColor(m.id, c);
                  setColorFor(null);
                  try {
                    await api.put(`/groups/${groupId}/members/${m.id}/color`, { color: c });
                  } catch (_) {
                    setMemberColor(m.id, before);
                  }
                }}
              />
            </div>
          )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Group card ───────────────────────────────────────────────────────────────
function GroupCard({ group, onLeft, onDeleted, onRenamed, T, L, baseLang }) {
  const [expanded, setExpanded] = useState(false);
  // Renaming, in place. The server has always allowed it (PATCH /groups/:id,
  // admins only); there was simply no way to ask for it from the app.
  const [renaming,   setRenaming]   = useState(false);
  const [renameText, setRenameText] = useState('');
  const cancelRenameRef = useRef(false);
  const [renameBlocked, setRenameBlocked] = useState(false);

  /**
   * Save the new name. Optimistic, and it puts the old one back if the save
   * fails — the calendar keeps whatever name it really has, never a name that
   * only exists on this screen.
   */
  async function saveRename() {
    if (cancelRenameRef.current) { cancelRenameRef.current = false; setRenaming(false); return; }
    const next = renameText.trim();
    setRenaming(false);
    if (!next || next === group.name) return;
    const before = group.name;
    onRenamed?.(group.id, next);
    try {
      // The client is imported as `api` in this file. It was `apiClient` here
      // and nowhere else, so renaming a shared calendar threw every time — and
      // the optimistic rename had already put the new name on screen, so it
      // looked like it had worked until the next reload.
      await api.patch(`/groups/${group.id}`, { name: next });
    } catch (err) {
      onRenamed?.(group.id, before);
      setActionError(err?.response?.data?.error || L.actionFailed);
    }
  }
  const [leaving, setLeaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState('');
  // null | 'leave' | 'delete' — the app's own confirmation, replacing the two
  // window.confirm boxes. These are the most destructive actions on the page,
  // and they were the last place still asking in the system's voice.
  const [confirming, setConfirming] = useState(null);
  const { status: copyStatus, copy } = useClipboard();

  async function doLeave() {
    setConfirming(null);
    setActionError('');
    setLeaving(true);
    try {
      await api.delete(`/groups/${group.id}/leave`);
      onLeft(group.id);
    } catch (err) {
      // It used to just stop spinning, which reads as "the app ignored me".
      setActionError(err?.response?.data?.error || L.actionFailed);
      setLeaving(false);
    }
  }

  async function doDelete() {
    setConfirming(null);
    setActionError('');
    setDeleting(true);
    try {
      await api.delete(`/groups/${group.id}`);
      onDeleted(group.id);
    } catch (err) {
      setActionError(err?.response?.data?.error || L.actionFailed);
      setDeleting(false);
    }
  }

  function handleShare() {
    const text = group.invite_code;
    if (!navigator.share) { copy(text); return; }
    navigator.share({ title: group.name, text }).catch((err) => {
      // Only AbortError means "changed my mind"; anything else is a real
      // failure and deserves the fallback rather than silence.
      if (err?.name !== 'AbortError') copy(text);
    });
  }

  return (
    <div style={{ borderBottom: '1px solid var(--color-border)', padding: '14px 0' }}>
      {/* Leave / delete confirmation, in the app's own voice */}
      {confirming && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}
          onClick={() => setConfirming(null)}
        >
          <DialogCard
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--color-card)', borderRadius: 'var(--radius)',
              padding: '22px 20px', width: '100%', maxWidth: 340,
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            }}
          >
            <p style={{ margin: '0 0 18px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text)', lineHeight: 1.45 }}>
              {confirming === 'delete' ? T.deleteConfirm : T.leaveConfirm}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Staying is the safe answer, so it comes first */}
              <button type="button" className="btn btn-outline" onClick={() => setConfirming(null)}>
                {T.cancel}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                style={{ borderColor: 'var(--color-red)', color: 'var(--color-red)' }}
                onClick={confirming === 'delete' ? doDelete : doLeave}
              >
                {confirming === 'delete' ? T.delete : T.leave}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      {/* Header */}
      <div
        onClick={() => { if (!renaming) setExpanded(e => !e); }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tap the name to rename it — admins only, which is the same rule
              the server enforces. Shown as plain text, not a field: a calendar
              name is a name until you decide otherwise, and a page full of
              input boxes looks like a form to fill in rather than a list of
              things you own. */}
          {renaming ? (
            <input
              autoFocus
              value={renameText}
              onClick={e => e.stopPropagation()}
              onChange={e => setRenameText(e.target.value)}
              onBlur={saveRename}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                // Escape has to win the race against the blur that follows it,
                // or "cancel" would save the very change it is cancelling.
                if (e.key === 'Escape') { cancelRenameRef.current = true; setRenaming(false); }
              }}
              style={{
                width: '100%', fontWeight: 700, fontSize: '0.92rem',
                fontFamily: 'inherit', color: 'var(--color-text)',
                background: 'var(--color-card)', borderRadius: 8,
                border: '1.5px solid var(--color-primary)', padding: '5px 8px',
              }}
            />
          ) : (
            <div
              onClick={e => {
                e.stopPropagation();
                if (!group.isAdmin) { setRenameBlocked(true); return; }
                setRenameText(group.name);
                setRenaming(true);
              }}
              title={group.isAdmin ? (RENAME_HINT[baseLang] || RENAME_HINT.en) : (RENAME_ONLY_ADMIN[baseLang] || RENAME_ONLY_ADMIN.en)}
              style={{
                fontWeight: 700, fontSize: '0.92rem', color: 'var(--color-text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                cursor: group.isAdmin ? 'text' : 'inherit',
              }}
            >
              {group.name}
            </div>
          )}
          {renameBlocked && !group.isAdmin && (
            <div role="status" style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 3, lineHeight: 1.4 }}>
              {RENAME_ONLY_ADMIN[baseLang] || RENAME_ONLY_ADMIN.en}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
            <span style={{
              fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 700,
              letterSpacing: '0.12em', color: 'var(--color-primary)',
            }}>
              {group.invite_code}
            </span>
            {group.isAdmin && (
              <span style={{
                fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: 'var(--color-primary)',
                border: '1px solid var(--color-primary)', borderRadius: 4, padding: '1px 6px',
              }}>
                {T.admin}
              </span>
            )}
          </div>
        </div>
        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
          <MemberList groupId={group.id} T={T} L={L} />

          {/* The code itself, big and selectable. Whatever happens with the
              clipboard, six characters can always be read out over the phone or
              typed by hand — so the code is never trapped inside a button that
              may or may not have worked. */}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 6 }}>
              {T.inviteCode}
            </div>
            <div style={{
              fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 800,
              letterSpacing: '0.28em', color: 'var(--color-primary-dark)',
              userSelect: 'all', WebkitUserSelect: 'all', padding: '4px 0',
            }}>
              {group.invite_code}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            <button
              onClick={handleShare}
              style={{
                background: 'none', border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)', padding: '9px 16px',
                fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text)',
                cursor: 'pointer', textAlign: 'center',
              }}
            >
              {copyStatus === 'ok' ? T.copied : T.shareInvite}
            </button>
            {copyStatus === 'fail' && (
              <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textAlign: 'center', lineHeight: 1.4 }}>
                {L.copyFailed}
              </div>
            )}
            {actionError && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-red)', textAlign: 'center', lineHeight: 1.4 }}>
                {actionError}
              </div>
            )}

            <button
              onClick={() => { setActionError(''); setConfirming('leave'); }}
              disabled={leaving}
              style={{
                background: 'none', border: '1.5px solid var(--color-red)',
                borderRadius: 'var(--radius-sm)', padding: '9px 16px',
                fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-red)',
                cursor: 'pointer', textAlign: 'center',
              }}
            >
              {leaving ? '…' : T.leave}
            </button>

            {group.isAdmin && (
              <button
                onClick={() => { setActionError(''); setConfirming('delete'); }}
                disabled={deleting}
                style={{
                  background: 'rgba(196,97,74,0.07)', border: '1.5px solid var(--color-red)',
                  borderRadius: 'var(--radius-sm)', padding: '9px 16px',
                  fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-red)',
                  cursor: 'pointer', textAlign: 'center',
                }}
              >
                {deleting ? '…' : T.delete}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create group form ────────────────────────────────────────────────────────
function CreateGroupForm({ onCreated, onCancel, T }) {
  const [name, setName]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/groups', { name: name.trim() });
      track('group_created'); // analytics: a shared calendar was created
      onCreated(data);
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_LIMIT_REACHED') {
        setError(T.planLimit || 'Hai raggiunto il limite di calendari condivisi del tuo piano.');
        setShowUpgrade(true);
      } else {
        setError(err.response?.data?.error || T.errorCreate);
      }
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 14 }}>
        <label style={{
          display: 'block', fontSize: '0.78rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--color-text-muted)', marginBottom: 6,
        }}>
          {T.groupNameLabel}
        </label>
        <input
          className="form-input"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={T.groupNamePlaceholder}
          aria-label={T.groupNamePlaceholder}
          maxLength={100}
          autoFocus
          disabled={loading}
        />
      </div>
      {error && (
        <p style={{ fontSize: '0.82rem', color: 'var(--color-red)', marginBottom: showUpgrade ? 4 : 12 }}>{error}</p>
      )}
      {/* "See the plans" is an invitation to buy, so it does not appear in the
          Play Store app — the error above already says the limit was reached. */}
      {showUpgrade && canShowPurchase() && (
        <p style={{ marginBottom: 12 }}>
          <a href="/settings?upgrade=1" style={{ fontSize: '0.82rem', color: 'var(--color-primary-dark)', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>⭐ {T.seePlans || 'Scopri i piani'}</a>
        </p>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1, padding: '10px 0', fontSize: '0.87rem', fontWeight: 600,
            background: 'none', border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)', color: 'var(--color-text)', cursor: 'pointer',
          }}
        >
          {T.cancel}
        </button>
        <button
          type="submit"
          disabled={loading || !name.trim()}
          style={{
            flex: 2, padding: '10px 0', fontSize: '0.87rem', fontWeight: 700,
            background: name.trim() && !loading ? 'var(--color-primary)' : 'var(--color-border)',
            border: 'none', borderRadius: 'var(--radius-sm)',
            color: name.trim() && !loading ? '#fff' : 'var(--color-text-muted)', cursor: 'pointer',
          }}
        >
          {loading ? T.creating : T.create}
        </button>
      </div>
    </form>
  );
}

// ─── Join group form ──────────────────────────────────────────────────────────
function JoinGroupForm({ onJoined, onCancel, T }) {
  const [code, setCode]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (code.trim().length < 6) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/groups/join', { invite_code: code.trim() });
      track('group_joined'); // analytics: joined a shared calendar via code
      onJoined(data);
    } catch (err) {
      // `errCode`, not `code`: `code` is the invite code the user typed, and
      // shadowing it here was a trap waiting for the next change.
      const errCode = err.response?.data?.code;
      const msg = err.response?.data?.error || T.errorJoin;
      if (errCode === 'CALENDAR_FULL') {
        setError(T.calendarFull || 'Questo calendario è al completo.');
      } else if (errCode === 'ALREADY_MEMBER' || msg.toLowerCase().includes('already')) {
        // The code is the reliable half; the English word is kept only so this
        // keeps working against a server that has not been updated yet.
        setError(T.errorAlready);
      } else {
        setError(msg);
      }
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 14 }}>
        <label style={{
          display: 'block', fontSize: '0.78rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--color-text-muted)', marginBottom: 6,
        }}>
          {T.inviteCode}
        </label>
        <input
          className="form-input"
          type="text"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          placeholder={T.inviteCodePlaceholder}
          aria-label={T.inviteCodePlaceholder}
          maxLength={6}
          style={{ fontFamily: 'monospace', fontSize: '1.15rem', letterSpacing: '0.2em', textAlign: 'center' }}
          autoFocus
          disabled={loading}
        />
      </div>
      {error && (
        <p style={{ fontSize: '0.82rem', color: 'var(--color-red)', marginBottom: 12 }}>{error}</p>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1, padding: '10px 0', fontSize: '0.87rem', fontWeight: 600,
            background: 'none', border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)', color: 'var(--color-text)', cursor: 'pointer',
          }}
        >
          {T.cancel}
        </button>
        <button
          type="submit"
          disabled={loading || code.trim().length < 6}
          style={{
            flex: 2, padding: '10px 0', fontSize: '0.87rem', fontWeight: 700,
            background: code.trim().length === 6 && !loading ? 'var(--color-primary)' : 'var(--color-border)',
            border: 'none', borderRadius: 'var(--radius-sm)',
            color: code.trim().length === 6 && !loading ? '#fff' : 'var(--color-text-muted)', cursor: 'pointer',
          }}
        >
          {loading ? T.joining : T.join}
        </button>
      </div>
    </form>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
// Cached across remounts so swiping back to Calendari shows the list instantly
// and refreshes in the background (not persisted).
let groupsCache = null;

export default function GroupsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fullT = useT(user?.language) || {};
  const T = fullT.groups || {};

  const baseLang = (user?.language || 'it').split('-')[0];

  const [groups, setGroups]   = useState(() => groupsCache || []);
  const [loading, setLoading] = useState(() => !groupsCache);
  const [sheet, setSheet]     = useState(null); // 'create' | 'join' | null

  const loadGroups = useCallback(() => {
    if (!groupsCache) setLoading(true);
    api.get('/groups')
      .then(r => { groupsCache = r.data || []; setGroups(groupsCache); })
      .catch(() => setGroups(groupsCache || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // Write-through: keep the module cache in sync after create/join/leave/delete,
  // so returning to Calendari shows the fresh list, not a stale cached one.
  useEffect(() => { groupsCache = groups; }, [groups]);

  // The server hands the list over already in alphabetical order. A calendar
  // that has just been created, joined or renamed has to land in the same
  // order straight away — otherwise it sits at the top until the next reload,
  // and the one place somebody looks for it is where the alphabet says it is.
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base', numeric: true });
  function handleCreated(group) { setGroups(prev => [...prev, group].sort(byName)); setSheet(null); }
  function handleJoined(group)  { setGroups(prev => [...prev, group].sort(byName)); setSheet(null); }
  function handleLeft(id)       { setGroups(prev => prev.filter(g => g.id !== id)); }
  function handleDeleted(id)    { setGroups(prev => prev.filter(g => g.id !== id)); }
  // Used both ways: to show the new name at once, and to put the old one back
  // if the save turns out to have failed.
  function handleRenamed(id, name) {
    setGroups(prev => prev.map(g => (g.id === id ? { ...g, name } : g)).sort(byName));
  }

  return (
    <div>
      {/* Page header. The back arrow is new and necessary: this page is no
          longer one of the sections along the bottom, so nothing in the bar
          lights up while you are here and there is no obvious way out. The
          way in is Settings, so the way out is Settings. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 16px' }}>
        <button
          type="button"
          onClick={() => navigate('/settings')}
          aria-label={T.back || 'Indietro'}
          style={{
            flexShrink: 0, width: 34, height: 34, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--color-primary)', color: '#fff',
            border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <BackIcon />
        </button>
        <h2 style={{ fontWeight: 800, fontSize: '1.05rem', margin: 0, color: 'var(--color-text)' }}>
          {T.pageTitle}
        </h2>
      </div>

      {/* "Famiglia" removed — sharing is now only via shared calendars (max 5 people) */}

      {/* The "connect your calendar" entry used to sit here, and it did not
          belong: this page is about calendars shared with other PEOPLE, and
          that button opens the read-only Apple feed — a different idea with an
          almost identical name. Settings has offered the same thing all along,
          under "Collega Apple", so this was a second door into one room, placed
          where it could only mislead. Removed rather than moved. */}

      {/* Action buttons — two distinct cards */}
      {!sheet && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          <button
            onClick={() => setSheet('create')}
            style={{
              padding: '14px 10px', fontSize: '0.85rem', fontWeight: 700,
              background: 'var(--color-primary)', border: 'none',
              borderRadius: 'var(--radius-sm)', color: '#fff',
              cursor: 'pointer', lineHeight: 1.3, textAlign: 'center',
            }}
          >
            {T.createGroup}
          </button>
          <button
            onClick={() => setSheet('join')}
            style={{
              padding: '14px 10px', fontSize: '0.85rem', fontWeight: 700,
              background: 'var(--color-card)',
              border: '2px solid var(--color-primary)',
              borderRadius: 'var(--radius-sm)', color: 'var(--color-primary)',
              cursor: 'pointer', lineHeight: 1.3, textAlign: 'center',
              boxShadow: 'var(--shadow)',
            }}
          >
            {T.joinGroup}
          </button>
        </div>
      )}

      {/* Inline form panel */}
      {sheet && (
        <div className="card" style={{ marginBottom: 16 }}>
          {sheet === 'create' && (
            <CreateGroupForm onCreated={handleCreated} onCancel={() => setSheet(null)} T={T} />
          )}
          {sheet === 'join' && (
            <JoinGroupForm onJoined={handleJoined} onCancel={() => setSheet(null)} T={T} />
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="spinner" style={{ margin: '48px auto' }} />
      ) : groups.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: 64, color: 'var(--color-text-muted)' }}>
          <div style={{ fontWeight: 700, fontSize: '0.97rem', marginBottom: 6, color: 'var(--color-text)' }}>
            {T.noGroups}
          </div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>{T.noGroupsSub}</div>
        </div>
      ) : (
        <div style={{ borderTop: '1px solid var(--color-border)' }}>
          {groups.map(g => (
            <GroupCard
              key={g.id}
              group={g}
              onLeft={handleLeft}
              onDeleted={handleDeleted}
              onRenamed={handleRenamed}
              baseLang={baseLang}
              T={T}
              L={LOCAL[baseLang] || LOCAL.en}
            />
          ))}
        </div>
      )}
    </div>
  );
}
